import React from 'react';
import { Button } from 'antd';
import type { ButtonProps } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';

// Extend ButtonProps and add your custom 'variant' prop
export type ButtonAppearance = 'default' | 'important' | 'icon';

interface AntdCustomButtonProps extends ButtonProps {
  appearance?: ButtonAppearance;
}

const AntdCustomButton: React.FC<AntdCustomButtonProps> = ({
  appearance = 'default',
  icon,
  children,
  className = '',
  style,
  disabled,
  ...rest
}) => {
  const getButtonProps = (): Pick<ButtonProps, 'type' | 'style'> => {
    if (disabled) {
      return {
        type: appearance === 'important' ? 'primary' : 'default',
        style: {
          color: 'rgba(0, 0, 0, 0.25)',
          backgroundColor: '#f5f5f5',
          borderColor: '#d9d9d9',
          cursor: 'not-allowed',
          ...style,
        },
      };
    }

    if (appearance === 'important') {
      return {
        type: 'primary',
        style: {
          color: '#ffffff',
          backgroundColor: '#3498DB',
          ...style,
        },
      };
    }

    if (appearance === 'icon') {
      return {
        type: 'default',
        style: {
          color: 'rgba(0, 0, 0, 0.88)',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          ...style,
        },
      };
    }

    return {
      type: 'default',
      style: {
        color: 'rgba(0, 0, 0, 0.88)',
        backgroundColor: '#ffffff',
        ...style,
      },
    };
  };

  const buttonProps = getButtonProps();
  const hasChildren = React.Children.count(children) > 0;
  const shouldShowGap = hasChildren || appearance === 'icon';

  return (
    <Button
      {...buttonProps}
      className={className}
      disabled={disabled}
      {...rest}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: shouldShowGap ? '8px' : '0',
        }}
      >
        {appearance === 'icon' ? (
          <>
            <PlusCircleOutlined style={{ fontSize: 14, width: 14 }} />
            <span>{children}</span>
          </>
        ) : (
          <>
            {icon}
            <span>{children}</span>
          </>
        )}
      </span>
    </Button>
  );
};

export default AntdCustomButton;
